import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shadcn/lib/utils';
import { ArrowRight, HelpCircle, History, MessageCirclePlus, Search, X } from 'lucide-react';
import type { AmbossMode } from './amboss.config';
import { AmbossActionBar, AmbossIconButton, AmbossPanel, AmbossPanelTitle } from './amboss.styles';
import { ModeButton, ModeToggle } from './AmbossSearchPanel.styles';
import { useAmboss } from './useAmboss';

export function AmbossSearchPanel() {
  const { t } = useTranslation();
  const { submit, showHelp, showHistory, close, mode: lastMode } = useAmboss();
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<AmbossMode>(lastMode);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const canSubmit = query.trim().length > 0;
  const doSubmit = () => {
    if (canSubmit) submit(query, mode);
  };

  return (
    <AmbossPanel
      role="dialog"
      aria-label={t('amboss.searchPanel.title')}
      onKeyDown={(e) => {
        if (e.key === 'Escape') close();
      }}
    >
      <div className="mb-3 flex items-center justify-between">
        <AmbossPanelTitle>{t('amboss.searchPanel.title')}</AmbossPanelTitle>
        <AmbossActionBar>
          <AmbossIconButton aria-label={t('amboss.actions.history')} onClick={showHistory}>
            <History size={20} />
          </AmbossIconButton>
          <AmbossIconButton aria-label={t('amboss.actions.help')} onClick={showHelp}>
            <HelpCircle size={20} />
          </AmbossIconButton>
          <AmbossIconButton aria-label={t('amboss.actions.close')} onClick={close}>
            <X size={20} />
          </AmbossIconButton>
        </AmbossActionBar>
      </div>

      <textarea
        ref={textareaRef}
        data-ph-no-autocapture
        className="min-h-24 w-full resize-none rounded-md border border-vantis-violet-muted p-3 text-sm outline-none focus:ring-2 focus:ring-ring"
        placeholder={t('amboss.searchPanel.placeholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            doSubmit();
          }
        }}
      />

      <div className="mt-3 flex items-center justify-between">
        <ModeToggle>
          <ModeButton $active={mode === 'ai'} onClick={() => setMode('ai')}>
            <MessageCirclePlus size={16} /> {t('amboss.searchPanel.aiMode')}
          </ModeButton>
          <ModeButton $active={mode === 'search'} $divider onClick={() => setMode('search')}>
            <Search size={16} /> {t('amboss.searchPanel.search')}
          </ModeButton>
        </ModeToggle>
        <button
          type="button"
          aria-label={t('amboss.searchPanel.submit')}
          disabled={!canSubmit}
          onClick={doSubmit}
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground',
            !canSubmit && 'opacity-50'
          )}
        >
          <ArrowRight size={20} />
        </button>
      </div>
    </AmbossPanel>
  );
}
